import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, Button, Row, Col, Table, Divider, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined, BarcodeOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import {
  UomUnit, UOM_PRESETS, UK_ORIGIN_OPTIONS, generateEan13, getCaseQuantity,
  resolveCasePrice, smartDefaultsForCategory, toNum,
} from '../../utils/wholesaleProduct';

const { Text } = Typography;
const { Option } = Select;

interface Props {
  form: FormInstance;
  /** Called when category changes to apply smart defaults */
  category?: string;
}

const WholesaleProductEngine: React.FC<Props> = ({ form, category }) => {
  const [uomRows, setUomRows] = useState<UomUnit[]>([]);

  const retailPrice = Form.useWatch('price', form);
  const wholesalePrice = Form.useWatch('wholesalePrice', form);
  const tierA = Form.useWatch('tierAPrice', form);
  const tierB = Form.useWatch('tierBPrice', form);
  const tierC = Form.useWatch('tierCPrice', form);
  const caseQty = Form.useWatch('caseQuantity', form);
  const casePrice = Form.useWatch('casePrice', form);

  useEffect(() => {
    if (!category) return;
    const defs = smartDefaultsForCategory(category);
    if (defs.taxRate != null && form.getFieldValue('taxRate') == null) form.setFieldValue('taxRate', defs.taxRate);
    if (defs.unit && !form.getFieldValue('unit')) form.setFieldValue('unit', defs.unit);
    if (defs.countryOfOrigin && !form.getFieldValue('countryOfOrigin')) {
      form.setFieldValue('countryOfOrigin', defs.countryOfOrigin);
    }
  }, [category, form]);

  useEffect(() => {
    const raw = form.getFieldValue('uomUnits');
    if (raw) {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed)) setUomRows(parsed);
      } catch { /* ignore */ }
    }
  }, [form]);

  const syncUomToForm = (rows: UomUnit[]) => {
    setUomRows(rows);
    form.setFieldValue('uomUnits', JSON.stringify(rows));
  };

  const addUomPreset = (preset: typeof UOM_PRESETS[0]) => {
    if (uomRows.some(r => r.code === preset.code)) return;
    const base = toNum(retailPrice);
    syncUomToForm([
      ...uomRows,
      {
        code: preset.code,
        label: preset.label,
        factor: preset.factor,
        price: preset.factor > 1 && base > 0 ? base * preset.factor : null,
        barcode: '',
      },
    ]);
  };

  const updateUomRow = (code: string, field: keyof UomUnit, value: string | number | null) => {
    syncUomToForm(uomRows.map(r => (r.code === code ? { ...r, [field]: value } : r)));
  };

  const removeUomRow = (code: string) => syncUomToForm(uomRows.filter(r => r.code !== code));

  const autoCasePrice = () => {
    const cq = toNum(caseQty);
    const wp = toNum(wholesalePrice) || toNum(retailPrice);
    if (cq > 0 && wp > 0) form.setFieldValue('casePrice', parseFloat((wp * cq).toFixed(2)));
  };

  const suggestedCase = resolveCasePrice(
    {
      price: retailPrice,
      wholesale_price: wholesalePrice,
      case_price: casePrice,
      case_quantity: caseQty,
    },
    toNum(wholesalePrice) || toNum(retailPrice),
    { isWholesale: true }
  );

  const generateBarcode = () => {
    const sku = form.getFieldValue('sku');
    const ean = generateEan13(sku || Date.now());
    form.setFieldValue('barcode', ean);
  };

  return (
    <>
      <Divider style={{ margin: '8px 0 16px' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>📦 Wholesale Trade Engine</span>
      </Divider>

      {/* Case / outer pricing */}
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="caseQuantity" label="Units per Case / Outer">
            <InputNumber style={{ width: '100%' }} min={1} step={1} placeholder="e.g. 24" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="casePrice" label="Case / Outer Price (£)">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} placeholder="Auto or manual" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
          <Button onClick={autoCasePrice} icon={<ThunderboltOutlined />}>Auto from unit price</Button>
        </Col>
      </Row>
      {suggestedCase != null && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
          Case price: <strong>£{suggestedCase.toFixed(2)}</strong>
          {getCaseQuantity({ case_quantity: caseQty }) > 0 && (
            <span style={{ color: '#64748b' }}> — {caseQty} units × trade unit price</span>
          )}
        </div>
      )}

      {/* Pricing tiers A/B/C */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Trade Pricing Tiers (per base unit)</Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        Assign customers to Tier A/B/C on their profile. Contract prices still override tiers.
      </Text>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="tierAPrice" label="Tier A — Best trade">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} addonAfter="GBP" placeholder={wholesalePrice ? String(wholesalePrice) : '0.00'} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="tierBPrice" label="Tier B — Standard trade">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} addonAfter="GBP" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="tierCPrice" label="Tier C — Entry trade">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} addonAfter="GBP" />
          </Form.Item>
        </Col>
      </Row>
      {(tierA || tierB || tierC) && (
        <div style={{ marginBottom: 16, fontSize: 12, color: '#64748b' }}>
          Margin vs retail (£{toNum(retailPrice).toFixed(2)}):
          {tierA ? ` A ${(((toNum(retailPrice) - toNum(tierA)) / toNum(retailPrice)) * 100 || 0).toFixed(0)}% off` : ''}
          {tierB ? ` · B ${(((toNum(retailPrice) - toNum(tierB)) / toNum(retailPrice)) * 100 || 0).toFixed(0)}% off` : ''}
          {tierC ? ` · C ${(((toNum(retailPrice) - toNum(tierC)) / toNum(retailPrice)) * 100 || 0).toFixed(0)}% off` : ''}
        </div>
      )}

      {/* Multi-UOM */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Units of Measure</Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
        Base unit is set in Stock section. Add outers, layers, pallets with conversion factors.
      </Text>
      <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {UOM_PRESETS.filter(p => p.code !== 'each').map(p => (
          <Button key={p.code} size="small" onClick={() => addUomPreset(p)} disabled={uomRows.some(r => r.code === p.code)}>
            + {p.label}
          </Button>
        ))}
      </div>
      <Form.Item name="uomUnits" hidden><Input /></Form.Item>
      {uomRows.length > 0 && (
        <Table size="small" pagination={false} rowKey="code" style={{ marginBottom: 16 }}
          dataSource={uomRows}
          columns={[
            { title: 'UOM', dataIndex: 'label', width: 120 },
            { title: '= Base units', dataIndex: 'factor', width: 110, render: (v, r: UomUnit) => (
              <InputNumber size="small" min={1} value={v} onChange={val => updateUomRow(r.code, 'factor', val || 1)} />
            )},
            { title: 'Price (£)', dataIndex: 'price', render: (v, r: UomUnit) => (
              <InputNumber size="small" min={0} step={0.01} value={v ?? undefined} placeholder="Auto"
                onChange={val => updateUomRow(r.code, 'price', val)} />
            )},
            { title: 'Barcode', dataIndex: 'barcode', render: (v, r: UomUnit) => (
              <Input size="small" value={v} placeholder="Optional"
                onChange={e => updateUomRow(r.code, 'barcode', e.target.value)} />
            )},
            { title: '', width: 44, render: (_, r: UomUnit) => (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeUomRow(r.code)} />
            )},
          ]}
        />
      )}

      {/* UK food compliance */}
      <Divider style={{ margin: '8px 0 16px' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>🇬🇧 UK Product Compliance</span>
      </Divider>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="netWeight" label="Net Weight">
            <Input placeholder="e.g. 500g, 1.5kg" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="grossWeight" label="Gross Weight (incl. packaging)">
            <Input placeholder="e.g. 550g, 1.6kg" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="countryOfOrigin" label="Country of Origin">
            <Select showSearch allowClear placeholder="Select country">
              {UK_ORIGIN_OPTIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* EAN-13 barcode */}
      <Row gutter={16} align="bottom">
        <Col xs={24} sm={16}>
          <Form.Item name="barcode" label="EAN-13 Barcode">
            <Input placeholder="Scan or generate" prefix={<BarcodeOutlined />} maxLength={13} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8} style={{ paddingBottom: 24 }}>
          <Button type="primary" icon={<ThunderboltOutlined />} onClick={generateBarcode} block>
            Generate EAN-13
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default WholesaleProductEngine;
